import {
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { lessThanOrEqualToValidator } from 'src/app/shared/validators/registration-validators';
import {
  equalDayMonthYear,
  monthsAgo,
  toAPIDate,
  yearsAgo,
} from 'src/app/utils/date';

export interface FilterDateModalData {
  startingDate?: Date;
  endingDate?: Date;
}

@Component({
  selector: 'app-modal',
  templateUrl: './filter-date-modal.component.html',
  styleUrls: ['./filter-date-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Modal molecule
 */
export class FilterDateModalComponent implements OnInit, OnDestroy {
  @Output() submitClicked = new EventEmitter<any>();
  form = new FormGroup({
    startingDate: new FormControl<Date | null>(null),
    endingDate: new FormControl<Date | null>(null),
    includeAddedPoints: new FormControl<boolean>(true),
    includeRedeemedPoints: new FormControl<boolean>(true),
    includeExpiredPoints: new FormControl<boolean>(true),
  });
  private subsink = new Subscription();
  labels = {
    date: 'date',
    last3Months: 'last-months',
    last6Months: 'last-months',
    lastYear: 'last-year',
    dateRange: 'specific-date-range',
    startingDate: 'starting-date',
    endingDate: 'ending-date',
    filterByDate: 'filter-by-date',
    clearFilter: 'clear-filters',
    cancel: 'cancel',
    errorCreatingReport: 'error-creating-report',
    ok: 'ok',
    successCreatingReport: 'success-creating-report',
  };
  activeFilter = {
    last3Months: false,
    last6Months: false,
    lastYear: false,
  };

  /**
   * @constructor
   */
  constructor(
    public dialogRef: MatDialogRef<FilterDateModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FilterDateModalData,
    public readonly translationService: TranslationService
  ) {}

  /**
   * on init
   */
  ngOnInit() {
    for (const key in this.data) {
      if (Object.hasOwnProperty.call(this.data, key)) {
        const value = (this.data as any)[key];
        if (value != null) {
          (this.form.controls as any)[key].setValue(value);
        }
      }
    }

    const endingDateControl = this.form.get('endingDate')!;
    const startingDateControl = this.form.get('startingDate')!;

    startingDateControl.addValidators(
      lessThanOrEqualToValidator(endingDateControl, 'invalid-starting-date')
    );

    this.subsink.add(
      endingDateControl.valueChanges.subscribe(() => {
        startingDateControl.updateValueAndValidity();
      })
    );

    this.subsink.add(
      startingDateControl.valueChanges.subscribe((value) => {
        if (value != null) {
          this.activateAppropriateFilter();
        } else if (endingDateControl.value == null) {
          this.last3Months();
        }
      })
    );
    this.subsink.add(
      endingDateControl.valueChanges.subscribe((value) => {
        if (value != null) {
          this.activateAppropriateFilter();
        } else if (startingDateControl.value == null) {
          this.last3Months();
        }
      })
    );
    Promise.all([
      this.translationService.translatePlural(this.labels.last6Months, 6),
      this.translationService.translatePlural(this.labels.last3Months, 3),
    ]).then(([s6, s3]) => {
      this.labels.last6Months = s6;
      this.labels.last3Months = s3;
      this.translationService.translateObject(this.labels);
    });

    this.last3Months();
  }

  /**
   * clean up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Action to create and download report
   */
  getDateFilter() {
    const startDateFromFilter =
      this.form.get('startingDate')!.value || undefined;
    const endDateFromFilter = this.form.get('endingDate')!.value || undefined;

    const startDate = startDateFromFilter
      ? toAPIDate(startDateFromFilter)
      : undefined;
    const endDate = endDateFromFilter
      ? toAPIDate(endDateFromFilter)
      : undefined;

    const obj = {
      startDate,
      endDate,
    };

    this.dialogRef.close(obj);
    this.submitClicked.emit(obj);
  }

  /**
   * Action to close the dialog
   */
  onCloseClick() {
    this.dialogRef.close();
  }

  /**
   * Action to clear inputs
   */
  onClearFilters() {
    this.form.reset();
  }

  /**
   * Sets date filters to three months ago
   */
  last3Months() {
    this.lastMonths(3);
    this.unsetFilters();
    this.activeFilter.last3Months = true;
  }

  /**
   * Sets date filters to six months ago
   */
  last6Months() {
    this.lastMonths(6);
    this.unsetFilters();
    this.activeFilter.last6Months = true;
  }

  /**
   * Sets date filter form control as some months ago
   * @param {number} months number must be from 1 to 11, inclusive.
   */
  lastMonths(months: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11) {
    const date = monthsAgo(months);
    this.form.get('startingDate')!.setValue(date);
    this.form.get('endingDate')!.setValue(new Date());
  }

  /**
   * Sets date filter form control as one year ago
   */
  lastYear() {
    const date = yearsAgo(1);
    this.form.get('startingDate')!.setValue(date);
    this.form.get('endingDate')!.setValue(new Date());
    this.unsetFilters();
    this.activeFilter.lastYear = true;
  }

  /**
   * unsets all date filters
   */
  private unsetFilters() {
    this.activeFilter.last3Months = false;
    this.activeFilter.last6Months = false;
    this.activeFilter.lastYear = false;
  }

  /**
   * activates the proper filter depending on starting and ending date
   */
  private activateAppropriateFilter() {
    const startingDate = this.form.controls['startingDate'].value;
    const endingDate = this.form.controls['endingDate'].value || new Date();
    if (!startingDate || !equalDayMonthYear(endingDate, new Date())) {
      return;
    }
    this.unsetFilters();
    if (equalDayMonthYear(startingDate, monthsAgo(3))) {
      this.activeFilter.last3Months = true;
    } else if (equalDayMonthYear(startingDate, monthsAgo(6))) {
      this.activeFilter.last6Months = true;
    } else if (equalDayMonthYear(startingDate, yearsAgo(1))) {
      this.activeFilter.lastYear = true;
    }
  }
}
